import React, { useState } from 'react'
import {
  Modal,
  Button,
  Icon,
  Spin,
  Alert,
  Upload,
  Avatar,
  Progress,
  Tabs,
  Input
} from 'antd'

const { TabPane } = Tabs

const VoiceCard = ({
  voice,
  isSelected,
  isPlaying,
  progress,
  onSelect,
  onPlay
}) => {
  const isUploaded = voice.type === 'uploaded'

  return (
    <div
      className={`voiceover-card ${
        isSelected ? 'voiceover-card--selected' : ''
      } ${isUploaded ? 'voiceover-card--uploaded' : ''}`}
      onClick={onSelect}
    >
      <div className="voiceover-card__visual">
        <Avatar
          size={40}
          icon={isUploaded ? 'user' : 'customer-service'}
          className={`voiceover-card__avatar ${
            isSelected ? 'voiceover-card__avatar--selected' : ''
          }`}
        />
        <div
          className={`voiceover-card__radio ${
            isSelected ? 'voiceover-card__radio--checked' : ''
          }`}
        >
          {isSelected && <div className="voiceover-card__radio-dot" />}
        </div>
      </div>

      <div className="voiceover-card__content">
        <span className="voiceover-card__name">{voice.name}</span>
        <span className="voiceover-card__type">
          {isUploaded ? 'Custom' : 'System'} Voice
        </span>
      </div>

      <div className="voiceover-card__actions">
        <div
          className={`voiceover-card__play-wrapper ${
            isPlaying ? 'voiceover-card__play-wrapper--playing' : ''
          }`}
          style={{
            position: 'relative',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            overflow: 'hidden'
          }}
        >
          {isPlaying && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `conic-gradient(#5057d5 ${progress}%, #eef0ff 0)`,
                zIndex: 0
              }}
            />
          )}
          <button
            className={`voiceover-card__play-btn ${
              isPlaying ? 'voiceover-card__play-btn--playing' : ''
            }`}
            onClick={e => {
              e.stopPropagation()
              onPlay()
            }}
            style={
              isPlaying
                ? {
                    position: 'absolute',
                    top: '3px',
                    left: '3px',
                    right: '3px',
                    bottom: '3px',
                    width: 'calc(100% - 6px)',
                    height: 'calc(100% - 6px)',
                    zIndex: 1,
                    borderRadius: '50%',
                    background: '#ffffff',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }
                : {}
            }
          >
            <Icon
              type={isPlaying ? 'pause' : 'caret-right'}
              style={isPlaying ? { color: '#5057d5', fontSize: '14px' } : {}}
            />
          </button>
        </div>
      </div>
    </div>
  )
}

const VoiceSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  voices,
  loadingVoices,
  handlePlayPreview,
  playingVoice,
  audioProgress,
  selectedVoiceId,
  targetName,
  handleUploadVoice,
  uploading,
  uploadProgress,
  uploadError,
  setUploadError,
  beforeUpload,
  fetchVoices,
  pendingUploadFile,
  pendingVoiceName,
  setPendingVoiceName,
  onRetryUpload,
  onOverwriteUpload
}) => {
  const [activeTab, setActiveTab] = useState('system')

  const systemVoices = voices.filter(v => v.type !== 'uploaded')
  const customVoices = voices.filter(v => v.type === 'uploaded')

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      title={`Select Voice for ${
        typeof targetName === 'object' ? targetName.name : targetName
      }`}
      centered
      className="voice-selection-modal"
      destroyOnClose
    >
      <div className="voice-selection-modal__content">
        <Tabs activeKey={activeTab} onChange={setActiveTab} animated={false}>
          <TabPane
            tab={
              <span>
                <Icon type=" глобальный" />
                System Voices
              </span>
            }
            key="system"
          >
            <div className="voice-selection-modal__tab-content">
              <div className="voice-selection-modal__header">
                <p>Choose from our library of high-quality AI voices.</p>
                <Button
                  type="link"
                  icon="reload"
                  onClick={fetchVoices}
                  loading={loadingVoices}
                >
                  Refresh
                </Button>
              </div>

              {loadingVoices ? (
                <div className="voice-selection-modal__loading">
                  <Spin tip="Loading voices..." />
                </div>
              ) : (
                <div className="voiceover-step__voice-grid">
                  {systemVoices.map(voice => (
                    <VoiceCard
                      key={voice.id}
                      voice={voice}
                      isSelected={selectedVoiceId === voice.id}
                      isPlaying={playingVoice === voice.id}
                      progress={playingVoice === voice.id ? audioProgress : 0}
                      onSelect={() => onSelect(voice.id)}
                      onPlay={() => handlePlayPreview(voice.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <Icon type="user" />
                Custom Voices
              </span>
            }
            key="custom"
          >
            <div className="voice-selection-modal__tab-content">
              <div className="voice-selection-modal__upload-section">
                {uploadError && (
                  <Alert
                    message={uploadError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setUploadError(null)}
                    style={{ marginBottom: 16 }}
                  />
                )}

                {uploadError && pendingUploadFile && (
                  <div style={{ marginBottom: 16 }}>
                    <Input
                      value={pendingVoiceName}
                      onChange={e => setPendingVoiceName(e.target.value)}
                      placeholder="Voice name"
                      style={{ marginBottom: 8 }}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button
                        type="primary"
                        onClick={onRetryUpload}
                        disabled={!pendingVoiceName || !pendingVoiceName.trim()}
                      >
                        Retry Upload
                      </Button>
                      <Button onClick={onOverwriteUpload} type="danger">
                        Overwrite Existing
                      </Button>
                    </div>
                  </div>
                )}

                {uploading ? (
                  <div className="voiceover-step__upload-loading">
                    <Spin tip="Uploading voice..." />
                    <Progress percent={uploadProgress} size="small" />
                  </div>
                ) : (
                  <Upload.Dragger
                    accept=".mp3"
                    customRequest={handleUploadVoice}
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                  >
                    <p className="ant-upload-drag-icon">
                      <Icon type="cloud-upload" />
                    </p>
                    <p className="ant-upload-text">
                      Click or drag audio file to upload
                    </p>
                    <p className="ant-upload-hint">MP3 file only</p>
                  </Upload.Dragger>
                )}
              </div>

              <div
                className="voiceover-step__voice-grid"
                style={{ marginTop: 24 }}
              >
                {customVoices.map(voice => (
                  <VoiceCard
                    key={voice.id}
                    voice={voice}
                    isSelected={selectedVoiceId === voice.id}
                    isPlaying={playingVoice === voice.id}
                    progress={playingVoice === voice.id ? audioProgress : 0}
                    onSelect={() => onSelect(voice.id)}
                    onPlay={() => handlePlayPreview(voice.id)}
                  />
                ))}
                {customVoices.length === 0 && !uploading && (
                  <div className="voice-selection-modal__empty">
                    <Icon type="info-circle" />
                    <p>No custom voices uploaded yet.</p>
                  </div>
                )}
              </div>
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  )
}

export default VoiceSelectionModal
